import { AccountInfo } from "@azure/msal-browser"
import { msalInstance } from "@/index"
import { loginRequest } from "@/config/authConfig"

/** Refresh slightly before JWT exp to avoid 401s from clock skew (common on long-lived TAZ sessions). */
export const TOKEN_EXPIRY_BUFFER_MS = 60_000

/** When JWT exp is missing, retry silent refresh on this interval. */
export const FALLBACK_PROACTIVE_REFRESH_MS = 10 * 60 * 1000

const PROACTIVE_REFRESH_FAILURE_BASE_MS = 5 * 60 * 1000
const PROACTIVE_REFRESH_FAILURE_MAX_MS = 30 * 60 * 1000

export const hasValidIdToken = (account: AccountInfo): boolean =>
    Boolean(
        account.idTokenClaims?.exp &&
            account.idTokenClaims.exp * 1000 > Date.now() + TOKEN_EXPIRY_BUFFER_MS
    )

export const getMsUntilProactiveRefresh = (account: AccountInfo): number | null => {
    const expSeconds = account.idTokenClaims?.exp
    if (!expSeconds) {
        return null
    }
    const refreshAtMs = expSeconds * 1000 - TOKEN_EXPIRY_BUFFER_MS
    return Math.max(0, refreshAtMs - Date.now())
}

type GetValidAccessTokenOptions = {
    /** Skip cached id token after a 401 — always call acquireTokenSilent. */
    forceRefresh?: boolean
}

export const getValidAccessToken = async (options?: GetValidAccessTokenOptions): Promise<string | null> => {
    const account = msalInstance.getActiveAccount()

    if (!account) {
        return null
    }

    if (!options?.forceRefresh) {
        const cachedToken = account.idToken
        if (cachedToken && hasValidIdToken(account)) {
            return cachedToken
        }
    }

    try {
        const tokenResponse = await msalInstance.acquireTokenSilent({ ...loginRequest, account })
        return tokenResponse.idToken ?? null
    } catch {
        // Silent refresh failed (session expired, network error, etc.).
        // Let the caller (or the 401 response interceptor) handle re-auth.
    }

    return null
}

let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null
let proactiveRefreshBound = false
/** Prevents a tight loop if MSAL account metadata lags after acquireTokenSilent. */
const MIN_PROACTIVE_REFRESH_INTERVAL_MS = 30_000
let lastProactiveRefreshAt = 0
let proactiveRefreshFailureCount = 0

const scheduleProactiveRefreshFailureRetry = (): void => {
    proactiveRefreshFailureCount += 1
    const backoffMs = Math.min(
        PROACTIVE_REFRESH_FAILURE_BASE_MS * proactiveRefreshFailureCount,
        PROACTIVE_REFRESH_FAILURE_MAX_MS
    )
    if (proactiveRefreshTimer) {
        clearTimeout(proactiveRefreshTimer)
    }
    proactiveRefreshTimer = setTimeout(() => {
        void runProactiveTokenRefresh()
    }, backoffMs)
}

const runProactiveTokenRefresh = async (): Promise<void> => {
    const now = Date.now()
    if (now - lastProactiveRefreshAt < MIN_PROACTIVE_REFRESH_INTERVAL_MS) {
        const waitMs = MIN_PROACTIVE_REFRESH_INTERVAL_MS - (now - lastProactiveRefreshAt)
        if (proactiveRefreshTimer) {
            clearTimeout(proactiveRefreshTimer)
        }
        proactiveRefreshTimer = setTimeout(() => {
            void runProactiveTokenRefresh()
        }, waitMs)
        return
    }
    lastProactiveRefreshAt = now

    const token = await getValidAccessToken({ forceRefresh: true })
    if (token) {
        proactiveRefreshFailureCount = 0
        scheduleProactiveTokenRefresh()
        return
    }

    scheduleProactiveRefreshFailureRetry()
}

export const scheduleProactiveTokenRefresh = (): void => {
    if (proactiveRefreshTimer) {
        clearTimeout(proactiveRefreshTimer)
        proactiveRefreshTimer = null
    }

    const account = msalInstance.getActiveAccount()
    if (!account) {
        return
    }

    const msUntilRefresh = getMsUntilProactiveRefresh(account)
    const delayMs = msUntilRefresh ?? FALLBACK_PROACTIVE_REFRESH_MS

    if (delayMs === 0) {
        proactiveRefreshTimer = setTimeout(() => {
            void runProactiveTokenRefresh()
        }, 0)
        return
    }

    proactiveRefreshTimer = setTimeout(() => {
        void runProactiveTokenRefresh()
    }, delayMs)
}

export const stopProactiveTokenRefresh = (): void => {
    if (proactiveRefreshTimer) {
        clearTimeout(proactiveRefreshTimer)
        proactiveRefreshTimer = null
    }
    lastProactiveRefreshAt = 0
    proactiveRefreshFailureCount = 0
}

const onVisibilityChange = (): void => {
    if (document.visibilityState !== "visible") {
        return
    }
    const account = msalInstance.getActiveAccount()
    if (!account) {
        return
    }
    if (!hasValidIdToken(account)) {
        void getValidAccessToken({ forceRefresh: true }).then(token => {
            if (token) {
                scheduleProactiveTokenRefresh()
            }
        })
    } else {
        scheduleProactiveTokenRefresh()
    }
}

const onLoginSuccess = (): void => {
    scheduleProactiveTokenRefresh()
}

/** Start timers/listeners to refresh the ID token before it expires (idle tabs, long TAZ sessions). */
export const bindProactiveTokenRefresh = (): void => {
    if (proactiveRefreshBound) {
        scheduleProactiveTokenRefresh()
        return
    }
    proactiveRefreshBound = true
    document.addEventListener("visibilitychange", onVisibilityChange)
    window.addEventListener("login-success", onLoginSuccess)
    scheduleProactiveTokenRefresh()
}

export const unbindProactiveTokenRefresh = (): void => {
    if (!proactiveRefreshBound) {
        return
    }
    proactiveRefreshBound = false
    document.removeEventListener("visibilitychange", onVisibilityChange)
    window.removeEventListener("login-success", onLoginSuccess)
    stopProactiveTokenRefresh()
}
